/*
 * Shared tree walker for the two diff engines (payload builder + state merger).
 *
 * Both engines walk the same `{id}` object tree; they only differ in what they
 * *do* at each node. This module is the shared substrate: it classifies every
 * node by `shape` (derived from the value) and `semantics` (derived from the
 * declarative field map at the node's property path), and yields
 * `(path, shape, semantics, [old, new])`. The strategy decides create/update/etc.
 * from `(shape, semantics, old, new)` - the walker never does.
 *
 * A node's `path` is the *property* path (dotted, no array-element ids) - that
 * is what the field map is keyed by. The full addressing path (including array
 * element ids) is carried separately as `name`/`arrayMask` for the state merger.
 */

// A field map maps a dotted property path to a semantics declaration.
//   'connect'   - relation, expressed by ID only (single object or array)
//   'create'    - owned, create/update/delete buckets
//   'writeOnly' - write-only: read is blank, an empty write is a no-op
//   'transient' - never persisted to the server; stripped from the payload
//   { type: 'create', project: { <inputField>: <sourcePath>, ... } }
//       - owned create with field projection (output-shape -> input-shape)
export type FieldMapEntry =
    | string
    | { type: 'create'; project?: Record<string, string> };
export type FieldMap = Record<string, FieldMapEntry>;

export type NodeShape = 'scalar' | 'file' | 'object' | 'array';
export type NodeSemantics = 'connect' | 'create' | 'writeOnly' | 'transient' | undefined;

export interface WalkNode {
    // property path (dotted, no array-element ids); '' at the root
    path: string;
    // full path segments, including array-element ids
    name: string[];
    // one entry per name segment; true where the segment is an array-element id
    arrayMask: boolean[];
    // 0 at the root; used by strategies to prune a subtree
    depth: number;
    shape: NodeShape;
    semantics: NodeSemantics;
    // present only when the map entry is a `create` with a `project`
    projection?: Record<string, string>;
    values: [unknown, unknown];
}

// Single predicate for File/Blob ("special" values). Used by the walker
// (classifies `file`), by deepCopy (passes through verbatim) and by the payload
// builder (sends verbatim). Replaces the scattered `instanceof` checks.
export function isSpecialValue(v: unknown): boolean {
    if (typeof File !== 'undefined' && v instanceof File) return true;
    if (typeof Blob !== 'undefined' && v instanceof Blob) return true;
    return false;
}

function isPrimitive(v: unknown): boolean {
    return v != null &&
        (typeof v === 'string' || typeof v === 'number' ||
         typeof v === 'boolean' || typeof v === 'bigint');
}

function isPlainObject(v: unknown): v is Record<string, unknown> {
    return v != null && typeof v === 'object' && !Array.isArray(v) && !isSpecialValue(v);
}

// Classify a node's shape from its (old, new) value pair.
// A File/Blob on either side is a `file`; a primitive on either side is a
// `scalar` (matching the legacy "either side primitive => leaf" rule); an array
// on either side is an `array`; otherwise, if either side is a non-null object
// (or both are null) it is an `object`.
export function classifyShape(oldV: unknown, newV: unknown): NodeShape {
    if (isSpecialValue(oldV) || isSpecialValue(newV)) return 'file';
    if (isPrimitive(oldV) || isPrimitive(newV)) return 'scalar';
    if (Array.isArray(oldV) || Array.isArray(newV)) return 'array';
    if (oldV != null || newV != null) return 'object';
    return 'scalar';
}

// Resolve a map entry into (semantics, projection).
export function resolveEntry(entry: FieldMapEntry | undefined): {
    semantics: NodeSemantics;
    projection?: Record<string, string>;
} {
    if (!entry) return { semantics: undefined };
    if (typeof entry === 'string') return { semantics: entry as NodeSemantics };
    return { semantics: entry.type, projection: entry.project };
}
function hasId(v: unknown): v is { id: unknown } {
    return v != null && typeof v === 'object' && 'id' in v;
}
function indexById(array: unknown[]): Record<string, unknown> {
    const out: Record<string, unknown> = {};
    (array || []).forEach((v) => {
        if (hasId(v)) {
            const id = v.id;
            if (id != null) {
                out[String(id)] = v;
            }
        }
    });
    return out;
}

function unionKeys(a: Record<string, unknown>, b: Record<string, unknown>): string[] {
    const seen: Record<string, boolean> = {};
    const out: string[] = [];
    for (const k of Object.keys(a)) {
        if (!seen[k]) { seen[k] = true; out.push(k); }
    }
    for (const k of Object.keys(b)) {
        if (!seen[k]) { seen[k] = true; out.push(k); }
    }
    return out;
}

// Walk the (old, new) tree, yielding every node in pre-order.
// `fieldMap` may be omitted (all semantics resolve to undefined).
export function* walkTree(
    oldV: unknown,
    newV: unknown,
    fieldMap: FieldMap = {},
): Generator<WalkNode> {
    yield* walkNode(oldV, newV, '', [], [], 0, fieldMap);
}

function* walkNode(
    oldV: unknown,
    newV: unknown,
    path: string,
    name: string[],
    arrayMask: boolean[],
    depth: number,
    fieldMap: FieldMap,
): Generator<WalkNode> {
    const shape = classifyShape(oldV, newV);
    const { semantics, projection } = resolveEntry(fieldMap[path]);
    yield { path, name, arrayMask, depth, shape, semantics, projection, values: [oldV, newV] };

    if (shape === 'scalar' || shape === 'file') return;

    if (shape === 'array') {
        const oldIdx = indexById(Array.isArray(oldV) ? oldV : []);
        const newIdx = indexById(Array.isArray(newV) ? newV : []);
        const ids = unionKeys(oldIdx, newIdx);
        for (const id of ids) {
            // an array element inherits the parent's property path
            yield* walkNode(
                oldIdx[id] ?? null,
                newIdx[id] ?? null,
                path,
                [...name, id],
                [...arrayMask, true],
                depth + 1,
                fieldMap,
            );
        }
        return;
    }

    // object
    const oldObj = isPlainObject(oldV) ? oldV : {};
    const newObj = isPlainObject(newV) ? newV : {};
    for (const key of unionKeys(oldObj, newObj)) {
        yield* walkNode(
            oldObj[key] ?? null,
            newObj[key] ?? null,
            path ? path + '.' + key : key,
            [...name, key],
            [...arrayMask, false],
            depth + 1,
            fieldMap,
        );
    }
}
